// Libraries
import { CommonModule } from '@angular/common';
import { Component, effect, inject, Input, OnDestroy, Signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Subject, takeUntil } from 'rxjs';

// Services
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { WebSocketService } from 'src/app/services/core/web-socket.service';

// Interfaces
import { defaultProject, Project } from 'src/app/interfaces/api/project';
import { User } from 'src/app/interfaces/api/user';

// Components
import { UserAvatarComponent } from 'projects/ui-library/src/lib/components/user-avatar/user-avatar.component';

interface UserInfo {
  name: string;
  avatar: string;
  email: string;
}

@Component({
  selector: 'app-socket-user-list',
  imports: [
    CommonModule,
    MatIconModule,
    UserAvatarComponent
  ],
  standalone: true,
  templateUrl: './socket-user-list.component.html',
  styleUrl: './socket-user-list.component.scss'
})

export class SocketUserListComponent implements OnDestroy {
  // Services
  private readonly socket: WebSocketService = inject(WebSocketService);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly authService: AuthService = inject(AuthService);

  // Component API
  @Input() sidenavOpen: boolean = false;

  // Variables
  connectedUsers: UserInfo[] = [];
  defaultAvatar: string = "/img/default-avatar.png";
  destroy$: Subject<void> = new Subject<void>();
  project: Project = defaultProject;

  //Signals
  readonly user: Signal<User> = this.authService.currentUser;
  readonly currentProject: Signal<Project> = this.projectService.currentProjectSignal;

  constructor() {
    // Effect: Project changes
    effect(() => {
      const project = this.currentProject();
      this.project = project;
      this.socket.setDatabase(project.database);
    });

    this.socket.getConnectedUsers().pipe(takeUntil(this.destroy$)).subscribe((users: UserInfo[]) => {
      const currentUser = this.user();
      const isCad42User = currentUser?.email?.includes("@cad42.com") ?? false;

      this.connectedUsers = currentUser ? users.filter(u =>
        u.name != (currentUser.firstName + " " + currentUser.lastName)
        && (isCad42User || !u.email?.includes("@cad42.com")))
        : users;
    });
  }

  trackByFn(index: number, user: UserInfo): string {
    return user.name;
  }

  getUserNames(): string {
    return this.connectedUsers.map(user => user.name).join('\n');
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
